/* TODO: move logic into ui/dropdowns/custom-dropdown (in a separate PR for clarity) */
import './index.css'
import * as React from 'react'
import { connect } from 'react-redux'
import type { DropdownOption } from 'types/ui'
import {
  getContactTags,
  getPlaylistTagsNames,
} from '@disco/state-management/selectors/tags'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Tag from 'components/ui/tag'

const mapStateToProps = (state, { tagsType, disabledTags }: Partial<Props>) => {
  const tags =
    tagsType && tagsType === 'contact'
      ? getContactTags(state)
      : getPlaylistTagsNames(state)
  const lowercaseDisabledTags = (disabledTags || []).map(dt => dt.toLowerCase())
  const filteredTags =
    disabledTags && disabledTags.length > 0
      ? tags.filter(t => !lowercaseDisabledTags.includes(t.toLowerCase()))
      : tags
  return {
    tags: filteredTags,
  }
}

function promptTextCreator(label: string) {
  return `Create tag "${label}"`
}

type Props = {
  onChange: (...args: Array<any>) => any
  onFocus?: (...args: Array<any>) => any
  selectedTags: string[] | string
  tags: string[]
  allowCreate?: boolean
  placeholder: string
  className?: string
  clearable?: boolean
  tagsType?: 'playlist' | 'contact'
  disabledTags?: string[]
  multi: boolean
  disabled?: boolean
  scrollContainer?: HTMLDivElement | null | undefined
  valueUnderDropdown?: boolean
  enableUp?: boolean
}

/**
 * TagSelector component for selecting and managing tags
 *
 * A connected React component that provides tag selection functionality with support
 * for both single and multi-select modes. Integrates with Redux to fetch available
 * tags and supports creating new tags, filtering disabled tags, and flexible
 * display options including value-under-dropdown mode.
 *
 * @param props - Component props
 * @param props.onChange - Callback function called when tag selection changes
 * @param props.onFocus - Optional callback for focus events
 * @param props.selectedTags - Currently selected tags (array or single string)
 * @param props.tags - Available tags from Redux state
 * @param props.allowCreate - Whether users can create new tags
 * @param props.placeholder - Placeholder text for the dropdown
 * @param props.className - Additional CSS class names
 * @param props.clearable - Whether the selection can be cleared
 * @param props.tagsType - Type of tags to fetch ('playlist' or 'contact')
 * @param props.disabledTags - Tags that should be filtered out/disabled
 * @param props.multi - Whether multiple tags can be selected
 * @param props.disabled - Whether the component is disabled
 * @param props.scrollContainer - Container element for scroll positioning
 * @param props.valueUnderDropdown - Whether to show selected tags below dropdown
 * @param props.enableUp - Whether dropdown can open upward
 * @returns JSX element containing the tag selector interface
 */
class TagSelector extends React.Component<Props> {
  static defaultProps = {
    multi: true,
  }

  _getTagOptions() {
    const { tags, valueUnderDropdown, selectedTags } = this.props
    let filterTags = tags

    if (valueUnderDropdown) {
      filterTags = tags.filter(t => !selectedTags.includes(t))
    }

    return filterTags.map(t => {
      return {
        value: t,
        label: `${t}`,
      }
    })
  }

  _renderValueUnderDropdown(selectedTagsArray) {
    const { disabled, valueUnderDropdown } = this.props
    return (
      valueUnderDropdown &&
      selectedTagsArray.length > 0 && (
        <div>
          {selectedTagsArray.map(({ value, label }) => {
            return (
              <div className="TagSelector_TagContainer" key={value}>
                <Tag
                  tag={label}
                  onRemoveClick={
                    disabled
                      ? undefined
                      : () => {
                          this.props.onChange(
                            selectedTagsArray.filter(t => t.value != value),
                          )
                        }
                  }
                />
              </div>
            )
          })}
        </div>
      )
    )
  }

  _getSelectedTagsOptions() {
    const { selectedTags } = this.props
    return Array.isArray(selectedTags)
      ? selectedTags.map(t => {
          return {
            value: t,
            label: `${t}`,
          }
        })
      : {
          value: selectedTags,
          label: `${selectedTags}`,
        }
  }

  render() {
    const { disabled, valueUnderDropdown } = this.props

    const selectedTagsOptions: DropdownOption[] | DropdownOption =
      this._getSelectedTagsOptions()

    const selectedTagsArray: DropdownOption[] = Array.isArray(
      selectedTagsOptions,
    )
      ? selectedTagsOptions
      : [selectedTagsOptions]

    const tagOptions: DropdownOption[] = this._getTagOptions()

    return (
      <>
        <CustomDropdown
          allowCreate={this.props.allowCreate}
          promptTextCreator={promptTextCreator}
          value={!valueUnderDropdown && selectedTagsOptions}
          placeholder={this.props.placeholder}
          multi={this.props.multi} // Temporarily disabled
          // clearable={!!this.props.clearable}
          options={tagOptions}
          onChange={
            valueUnderDropdown
              ? tags => {
                  this.props.onChange([...selectedTagsArray, ...tags])
                }
              : this.props.onChange
          }
          disabled={disabled}
          scrollContainer={this.props.scrollContainer}
          enableUp={this.props.enableUp}
        />

        {this._renderValueUnderDropdown(selectedTagsArray)}
      </>
    )
  }
}

export default connect(mapStateToProps, {})(TagSelector) as any
